'use client';

import { BiHome, BiSupport } from 'react-icons/bi';
import { RiRobot2Line } from 'react-icons/ri';
import { HiOutlineSparkles } from 'react-icons/hi2';
import { useTranslation } from '@/hooks/useTranslation';
import LouiseButton from '@/components/buttons/LouiseButton';
import React from 'react';

interface PageContentProps {
  locale: string;
}

export default function Custom404({ locale }: PageContentProps) {
  const { t } = useTranslation(locale);

  return (
    <div className="tw-min-h-screen tw-pt-10 lg:tw-pt-10 tw-bg-gradient-to-br tw-from-gray-50 tw-via-white tw-to-blue-50 tw-font-['Lexend'] tw-overflow-hidden">
      {/* Background Effects */}
      <div className="tw-absolute tw-inset-0">
        <div className="tw-absolute tw-top-1/4 tw-left-1/4 tw-w-96 tw-h-96 tw-bg-blue-400/10 tw-rounded-full tw-blur-3xl tw-animate-pulse"></div>
        <div
          className="tw-absolute tw-bottom-1/4 tw-right-1/4 tw-w-80 tw-h-80 tw-bg-purple-400/10 tw-rounded-full tw-blur-3xl tw-animate-pulse"
          style={{ animationDelay: '2s' }}
        ></div>
      </div>

      <div className="tw-relative tw-z-10 tw-min-h-screen tw-flex tw-flex-col">
        {/* Main Content */}
        <div className="tw-flex-grow tw-flex tw-items-center tw-justify-center tw-px-4 sm:tw-px-6 lg:tw-px-8">
          <div className="tw-max-w-4xl tw-mx-auto tw-text-center tw-space-y-12">
            {/* Error Display */}
            <div className="tw-space-y-8">
              {/* Animated 404 */}
              <div className="tw-relative">
                <div className="tw-text-8xl sm:tw-text-9xl lg:tw-text-[12rem] tw-font-black tw-text-transparent tw-bg-clip-text tw-bg-gradient-to-r tw-from-blue-600 tw-to-purple-600 tw-leading-none tw-select-none">
                  404
                </div>
                {/* Floating icons */}
                <div className="tw-absolute tw-top-0 tw-left-1/4 tw-animate-bounce" style={{ animationDelay: '0.5s' }}>
                  <HiOutlineSparkles className="tw-w-8 tw-h-8 tw-text-blue-400" />
                </div>
                <div
                  className="tw-absolute tw-bottom-0 tw-right-1/4 tw-animate-bounce"
                  style={{ animationDelay: '1.5s' }}
                >
                  <RiRobot2Line className="tw-w-10 tw-h-10 tw-text-purple-400" />
                </div>
              </div>

              {/* Error Message */}
              <div className="tw-space-y-4">
                <h1 className="tw-text-2xl sm:tw-text-3xl lg:tw-text-4xl tw-font-bold tw-text-gray-900">
                  {t('Error404/title')}
                </h1>
                <h2 className="tw-text-lg tw-text-gray-600 tw-max-w-2xl tw-mx-auto tw-leading-relaxed">
                  {t('Error404/description')}
                </h2>
              </div>

              {/* Primary CTA */}
              <div className="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-justify-center tw-items-center">
                <LouiseButton
                  color="blue"
                  cta={t('Error404/btn')}
                  ctaTarget="home"
                  gradiant={true}
                  icon={<BiHome className="tw-w-5 tw-h-5" />}
                  locale={locale}
                />
                <LouiseButton
                  color="white"
                  cta={t('Error404/contact-support')}
                  ctaTarget="contact"
                  gradiant={true}
                  icon={<BiSupport className="tw-w-5 tw-h-5" />}
                  locale={locale}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
